import React, { useEffect, useState } from "react";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { PageLoading } from "../../components";
import { StepNumberCircle } from "./WorkflowSetupTabs";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { UpdateVendorArgs } from "../data-map/components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { Api } from "../../../common/service/Api";
import {
  Modal,
  SelectMenuDropdown,
  SimpleCheckbox,
  Slideout,
  TextInput,
  Toggle,
  ToolTip,
} from "@truevault/aquilo";
import { NetworkRequest } from "../../../common/models";
import { DataMapDto } from "../../../common/service/server/dto/datamap/DataMapDto";
import { useSnackBar, ExplanatoryCard } from "@truevault/aquilo";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { Routes } from "../../root/routes";
import { useHistory } from "react-router";
import {
  getSensitivePICsWithFilters,
  INFERENCES_BASED_ON_SENSITIVE_INFORMATION_PIC_KEY,
} from "../../../common/service/resources/data-map/picHelpers";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { BoltIcon, BoltSlashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import { RequestHandlingInstructionDto } from "../../../common/service/server/dto/RequestHandlingInstructionDto";
import { Form } from "react-final-form";
import { useRequestInstructions } from "../../hooks/useOrganizationVendors";
import clsx from "clsx";
import _ from "lodash";
import { isEventClickTargetOnSelectMenu } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import { alphabetically } from "../../surveys/util";
import { useIntegrationsSettings } from "../../components/superorg/settings/IntegrationsSettings";
import {
  BUSINESS_SURVEY_NAME,
  KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG,
} from "../../surveys/businessSurvey";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { PrivacyCenterIntegrationsDto } from "../../../common/service/server/dto/PrivacyCenterIntegrationsDto";

export type VendorsAndDisclosedPICsForLimitRequests = {
  /**
   * Whether the legacy way of determining limit vendors should be used.
   * If this is true, none of the other fields in this type are applicable and should be ignored.
   */
  shouldUseLegacyLimitVendors: boolean;

  /**
   * Whether limit requests are enabled for the org.
   */
  isLimitRequestsEnabled: boolean;

  /**
   * Vendor IDs that are required to process limit requests.
   * By definition, this list is empty if `isLimitRequestsEnabled` is false.
   */
  vendorIdsForProcessingLimitRequests: string[];

  // A vendor can trigger a limit request for one of two reasons:
  //   1. They are being disclosed a sensitive PIC and user has responded that they are using that sensitive PIC for non-essential purposes.
  //   2. They are being disclosed a sensitive PIC that always triggers a limit request without requiring a user response.
  // The following two fields are mutually exclusive list of vendors for each of these two cases.
  // The union of the two lists is `vendorIdsForProcessingLimitRequests`.
  // Note that the second condition overrides the first if a vendor can be in both lists.
  vendorIdsUsingSensitivePICForNonEssentialPurposes: string[];
  vendorIdsAutomaticallyTriggerLimitRequest: string[];

  // Note: All remaining fields are used for configuring limit requests

  /**
   * Vendor IDs that are being disclosed a PIC that may trigger a limit request.
   */
  vendorIdsProcessingSensitivePICs: string[];

  /**
   * List of unique PIC IDs that are being disclosed that may trigger a limit request.
   */
  processedSensitivePICIds: string[];

  /**
   * Vendors that require a user response to how they are using their sensitive PICs.
   * Note that this includes vendors that already have a response.
   */
  vendorIdsRequiringResponseForNonEssentialSpiQuestion: string[];

  /**
   * Whether all vendors requiring a response have a response.
   */
  hasAllRequiredResponses: boolean;
};

/**
 * Retrieves information about vendors and PICs that are relevant for the Request to Limit form.
 *
 * @returns {[string[], string[], Map<string, string[]>]} An array with three elements:
 *  - Vendor IDs that are being disclosed a sensitive PIC.
 *  - The list of unique sensitive PIC IDs that are being disclosed.
 *  - A map of vendor IDs to a list of PIC keys that are sensitive and disclosed to that vendor. Note that this does not include PICs without a PIC key, and a vendor might be missing if none of their disclosed PICs have a PIC key.
 */
const getVendorsAndPICsForLimitRequests = (
  dataMap: DataMapDto,
): [string[], string[], Map<string, string[]>] => {
  if (!dataMap) {
    return [[], [], new Map()];
  }

  const allSensitivePICIds = getSensitivePICsWithFilters(dataMap, {
    includeInferencesBasedOnSensitiveInformationPIC: true,
  }).map((pic) => pic.id);

  const processedSensitivePICIds = new Set<string>();
  const vendorIdsProcessingSensitivePICs = new Set<string>();
  const vendorIdToPICKeys = new Map<string, string[]>();

  dataMap.disclosure.forEach((disclosure) => {
    const collectionGroup = dataMap.collectionGroups[disclosure.groupId];
    const pic = dataMap.categories[disclosure.picId];
    if (!collectionGroup || !pic) return;

    if (allSensitivePICIds.includes(pic.id) && collectionGroup.type !== "EMPLOYMENT") {
      processedSensitivePICIds.add(pic.id);
      vendorIdsProcessingSensitivePICs.add(disclosure.vendorId);

      if (pic?.key?.length > 0) {
        if (
          vendorIdToPICKeys.has(disclosure.vendorId) &&
          !vendorIdToPICKeys.get(disclosure.vendorId)?.includes(pic.key)
        ) {
          vendorIdToPICKeys.set(disclosure.vendorId, [
            ...vendorIdToPICKeys.get(disclosure.vendorId),
            pic.key,
          ]);
        } else {
          vendorIdToPICKeys.set(disclosure.vendorId, [pic.key]);
        }
      }
    }
  });

  return [
    Array.from(vendorIdsProcessingSensitivePICs),
    Array.from(processedSensitivePICIds),
    vendorIdToPICKeys,
  ];
};

/**
 * @param {OrganizationDataRecipientDto[]} orgDataRecipients - The full, un-filtered list of the organization's data recipients.
 * @param {DataMapDto} dataMap - The data map associated with the organization.
 * @param {Map<string, boolean>} [spiNonEssentialPurposesResponseByVendorId] - Optional map to override the spiUsedForNonEssentialPurposes value for the given vendor ID.
 */
export const getAllInformationForLimitRequests = (
  orgDataRecipients: OrganizationDataRecipientDto[],
  dataMap: DataMapDto,
  spiNonEssentialPurposesOverrideByVendorId?: Map<string, boolean>,
): VendorsAndDisclosedPICsForLimitRequests => {
  const [vendorIdsProcessingSensitivePICs, processedSensitivePICIds, vendorIdToPICKeys] =
    getVendorsAndPICsForLimitRequests(dataMap);

  // First, partition the vendors into those that (1) require a response to the non-essential SPI purposes question and (2) vendors that are being diclosed a PIC that always triggers a limit request
  const [vendorsRequiringNonEssentialSpiResponse, vendorsAlwaysTriggeringLimit] = _.partition(
    (orgDataRecipients || []).filter((dr) =>
      vendorIdsProcessingSensitivePICs.includes(dr.vendorId),
    ),
    (dr) => {
      // The only reason a vendor doesn't require a response is if they are disclosed the "Inferences based on sensitive information" PIC, since for that PIC we auto-select and auto-lock the non-essential purposes answer to "Yes" (POL-5948)
      let isDisclosedSensitiveInferencesPic =
        vendorIdToPICKeys.has(dr.vendorId) &&
        vendorIdToPICKeys
          .get(dr.vendorId)
          .includes(INFERENCES_BASED_ON_SENSITIVE_INFORMATION_PIC_KEY);

      // Returning true from the prediction lambda will place the result in the first list, which is vendors that do require a response
      return !isDisclosedSensitiveInferencesPic;
    },
  );

  const vendorsWithYesResponse = vendorsRequiringNonEssentialSpiResponse.filter((dr) => {
    let response = spiNonEssentialPurposesOverrideByVendorId?.has(dr.vendorId)
      ? spiNonEssentialPurposesOverrideByVendorId.get(dr.vendorId)
      : dr.spiUsedForNonEssentialPurposes;
    return response === true;
  });
  const vendorsWithMissingResponse = vendorsRequiringNonEssentialSpiResponse.filter((dr) => {
    let response = spiNonEssentialPurposesOverrideByVendorId?.has(dr.vendorId)
      ? spiNonEssentialPurposesOverrideByVendorId.get(dr.vendorId)
      : dr.spiUsedForNonEssentialPurposes;
    return response === null || response === undefined;
  });

  // Legacy way of determining limit vendors, which occurs when vendors requiring a response have partial responses that are all No and have no auto-triggering sensitive PIC, and can return early if so
  const shouldUseLegacyLimitVendors =
    vendorsAlwaysTriggeringLimit.length == 0 &&
    vendorsWithYesResponse.length == 0 &&
    vendorsWithMissingResponse.length > 0;

  const isLimitRequestsEnabled =
    vendorsAlwaysTriggeringLimit.length > 0 || vendorsWithYesResponse.length > 0;

  const vendorIdsForProcessingLimitRequests = [
    ...vendorsWithYesResponse.map((dr) => dr.vendorId),
    ...vendorsAlwaysTriggeringLimit.map((dr) => dr.vendorId),
  ];

  return {
    isLimitRequestsEnabled,
    shouldUseLegacyLimitVendors,
    hasAllRequiredResponses: vendorsWithMissingResponse.length == 0,
    vendorIdsForProcessingLimitRequests,
    vendorIdsProcessingSensitivePICs,
    processedSensitivePICIds,
    vendorIdsRequiringResponseForNonEssentialSpiQuestion:
      vendorsRequiringNonEssentialSpiResponse.map((dr) => dr.vendorId),
    vendorIdsUsingSensitivePICForNonEssentialPurposes: vendorsWithYesResponse.map(
      (dr) => dr.vendorId,
    ),
    vendorIdsAutomaticallyTriggerLimitRequest: vendorsAlwaysTriggeringLimit.map(
      (dr) => dr.vendorId,
    ),
  };
};

export const LimitInstructionsPage = () => {
  const history = useHistory();
  const snackbar = useSnackBar();

  const debugQueryParam = new URLSearchParams(window.location.search).get("debug");
  const showDiagnostics = debugQueryParam === "1" || debugQueryParam === "2";
  const showVerboseDiagnostics = debugQueryParam === "2";

  const [organization, , refreshOrg] = usePrimaryOrganization();
  const [allVendors, vendorsRequest, fetchVendors] = useOrganizationVendors(
    organization?.id,
    // All following args are default values from function signature, except for onlyUseSettled
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ true,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );
  const { data: dataMap, request: dataMapRequest } = useDataMap(organization.id);
  const { answers: bizSurveyAnswers, request: bizSurveyRequest } = useSurvey(
    organization?.id,
    BUSINESS_SURVEY_NAME,
  );

  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);

  const [
    autoSendRequestEmailNotificationsForLimitRequestsValue,
    setAutoSendRequestEmailNotificationsForLimitRequestsValue,
  ] = useState(null);

  useEffect(() => {
    if (organization) {
      setAutoSendRequestEmailNotificationsForLimitRequestsValue(
        organization.autoSendRequestEmailNotificationsForLimitRequests,
      );
    }
  }, [organization]);

  const [instructions, setInstructions] = useState<null | RequestHandlingInstructionDto[]>(null);
  const [, instructionsRequest, fetchInstructions] = useRequestInstructions(
    organization.id,
    "LIMIT",
    (data: RequestHandlingInstructionDto[]) => {
      setInstructions(data);
    },
  );
  const { fetch: updateInstruction } = useActionRequest({
    api: async (newInstruction: RequestHandlingInstructionDto) => {
      await Api.requestHandlingInstructions.putInstruction(organization?.id, newInstruction);
    },
    onSuccess: fetchInstructions,
    // onError: () => {
    //   enqueueSnackbar(`Error updating vendor deletion settings`, { variant: "error" });
    // },
  });

  const { result: vendorMessageTemplatePreview } = useDataRequest({
    queryKey: ["vendorMessageTemplatePreview", organization?.id, "limit"],
    api: () =>
      Api.dataRequest.getRequestContactVendorsPreviewMessageTemplateForRequestType(
        organization?.id,
        "limit",
      ),
  });

  const [showListOfSensitivePicsModal, setShowListOfSensitivePicsModal] = useState(false);

  const [showEssentialPurposesModal, setShowEssentialPurposesModal] = useState(false);

  const { fetch: updateDataRecipient } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(organization?.id, vendorId, vendorSettings);
    },
    onSuccess: fetchVendors,
    // onError: () => {
    //   enqueueSnackbar(`Error updating vendor data accessibility settings`, { variant: "error" });
    // },
  });

  const { settings: apiIntegrations } = useIntegrationsSettings();

  // This map duplicates the data from the vendors objects, but it is updated opportunistically, i.e. without having to wait for a vendor re-fetch to finish
  // Note that because this is NOT the source of truth for the vendor settings, we only use this for displaying real-time UI feedback, and if an update fails, may fall out of sync with the vendors
  let [answersByVendorId, setAnswersByVendorId] = useState<Map<string, boolean> | null>(null);

  // Initialize `answersByVendorId` once the vendors and data map are loaded
  useEffect(() => {
    if (!allVendors || !dataMap) {
      return;
    }

    if (answersByVendorId === null) {
      const [vendorIdsProcessingSensitivePICs] = getVendorsAndPICsForLimitRequests(dataMap);
      const limitVendors = (allVendors || []).filter((v) =>
        vendorIdsProcessingSensitivePICs.includes(v.vendorId),
      );
      let initMap = new Map<string, boolean>(
        limitVendors
          .filter(
            (v) =>
              v.spiUsedForNonEssentialPurposes !== undefined &&
              v.spiUsedForNonEssentialPurposes !== null,
          )
          .map(
            (v) => [v.vendorId, v.spiUsedForNonEssentialPurposes as boolean] as [string, boolean],
          ),
      );
      setAnswersByVendorId(initMap);
    }
  }, [allVendors, dataMap, answersByVendorId, setAnswersByVendorId]);

  const limitRequestInfo = getAllInformationForLimitRequests(
    allVendors,
    dataMap,
    answersByVendorId,
  );
  const {
    isLimitRequestsEnabled,
    vendorIdsProcessingSensitivePICs,
    processedSensitivePICIds,
    vendorIdsAutomaticallyTriggerLimitRequest,
    hasAllRequiredResponses,
  } = { ...limitRequestInfo };

  const sensitiveCollectedPICNames = Object.values(dataMap?.categories || {})
    .filter((cat) => processedSensitivePICIds.includes(cat.id))
    .map((cat) => cat.name);

  const childrenUnder16DataCollectionEnabled =
    bizSurveyAnswers?.[KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG] === "true";

  const allSensitivePICNames = [
    ...sensitiveCollectedPICNames,
    ...(childrenUnder16DataCollectionEnabled ? ["Data from children under 16"] : []),
  ];

  // Get vendor IDs that are disclosed ANY data from non-employment collection groups
  // These are relevant for children under 16 SPI since any vendor receiving consumer data could be processing children's data
  const vendorIdsWithNonEmploymentDisclosures = new Set<string>();
  dataMap?.disclosure?.forEach((disclosure) => {
    const collectionGroup = dataMap.collectionGroups[disclosure.groupId];
    if (collectionGroup && collectionGroup.type !== "EMPLOYMENT") {
      vendorIdsWithNonEmploymentDisclosures.add(disclosure.vendorId);
    }
  });

  // Show vendors disclosed sensitive PICs, plus (if children under 16 data is collected) vendors disclosed any non-employment data
  let limitVendors = (allVendors || [])
    .filter(
      (v) =>
        vendorIdsProcessingSensitivePICs.includes(v.vendorId) ||
        (childrenUnder16DataCollectionEnabled &&
          vendorIdsWithNonEmploymentDisclosures.has(v.vendorId)),
    )
    .sort(alphabetically);

  // Check if any vendor in the children under 16 context has answered "Yes" to using SPI for profiling/marketing
  // Fall back to checking vendor's spiUsedForNonEssentialPurposes directly if not in answersByVendorId (e.g., on initial page load)
  const hasYesResponseForChildrenUnder16 =
    childrenUnder16DataCollectionEnabled &&
    limitVendors.some(
      (v) =>
        answersByVendorId?.get(v.vendorId) === true || v.spiUsedForNonEssentialPurposes === true,
    );

  // Limit requests are enabled if either PIC-based logic triggers it OR children under 16 vendors have a "Yes" response
  const effectiveIsLimitRequestsEnabled =
    isLimitRequestsEnabled || hasYesResponseForChildrenUnder16;

  if (
    !organization ||
    !dataMap ||
    allVendors === undefined ||
    !NetworkRequest.areFinished(
      vendorsRequest,
      dataMapRequest,
      instructionsRequest,
      bizSurveyRequest,
    )
  ) {
    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  if (
    allVendors !== undefined &&
    vendorIdsProcessingSensitivePICs.length === 0 &&
    !childrenUnder16DataCollectionEnabled
  ) {
    return (
      <WorkflowSetupPageWrapper
        selectedTabKey="request-to-limit"
        nextOnClick={async () => {
          history.push(Routes.organization.requestHandling.requestToProfilingOptOut);
        }}
        nextDisabled={false}
      >
        <div className="tw-boundary">
          <div className="text-neutral-600 font-semibold mt-12 max-w-[924px]">
            The Request to Limit applies to certain uses of{" "}
            <span
              className="text-blue-600 font-semibold underline cursor-pointer"
              onClick={() => setShowListOfSensitivePicsModal(true)}
            >
              Sensitive Personal Information (SPI)
            </span>
            . According to your{" "}
            <a className="text-blue-600 font-semibold underline" href={Routes.dataMap.root}>
              Data Map
            </a>
            , you do not collect any categories of SPI. Update your Data Map if this is incorrect.
          </div>
        </div>

        {showListOfSensitivePicsModal && (
          <ListOfSensitivePicsModal
            dataMap={dataMap}
            closeModal={() => setShowListOfSensitivePicsModal(false)}
          />
        )}

        {showDiagnostics && (
          <LimitInstructionsDiagnostics
            allVendors={allVendors}
            limitRequestInfo={limitRequestInfo}
            dataMap={dataMap}
            showVerboseDiagnostics={showVerboseDiagnostics}
          />
        )}
      </WorkflowSetupPageWrapper>
    );
  }

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="request-to-limit"
      nextOnClick={async () => {
        history.push(Routes.organization.requestHandling.requestToProfilingOptOut);
      }}
      nextDisabled={!hasAllRequiredResponses}
      hideFooter={sidebarOpenForVendorId != null}
    >
      <div className="tw-boundary">
        <div className="max-w-[893px] ml-[40px]">
          <ExplanatoryCard
            topBody={
              <div>
                <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                  What is a Request to Limit?
                </h3>
                <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                  <p>
                    A consumer's request that your business stop using any of their sensitive
                    personal information for unnecessary purposes, such as for marketing, sales,
                    advertising, or creating customer profiles.
                  </p>
                </div>
              </div>
            }
            expandable={false}
          />
        </div>

        <div className="flex items-center gap-3 mt-10">
          <StepNumberCircle text="1" />
          <div className="text-neutral-900 text-[18px] font-normal">
            Do you use Sensitive Personal Information (SPI) for profiling or marketing?
          </div>
        </div>

        <div className="text-neutral-900 text-[14px] pl-[40px] mt-1.5 max-w-[776px]">
          <div className="bg-blue-50 flex items-flex-start max-w-[736px] my-[6px] p-[16px] rounded-[8px]">
            <div>
              <InformationCircleIcon className="w-[16px] h-[16px] text-blue-400 mr-[14px]" />
            </div>
            <div className="text-[14px]">
              <div className="text-blue-700 font-medium">
                You collect the following Sensitive Personal Information (SPI) from consumers:
              </div>
              <div className="text-blue-600 font-normal">
                <ul className="list-disc list-inside">
                  {allSensitivePICNames.map((name, i) => (
                    <li key={i}>{name}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-[16px] text-[14px] text-blue-600">
                Is this inaccurate? Update your{" "}
                <a className="text-blue-700 font-medium" href={Routes.dataMap.root}>
                  Data Collection answers
                </a>
                .
              </div>
            </div>
          </div>
          <p>SPI may be processed/analyzed/stored with the vendors listed in the table below.</p>
          <p className="mt-2">
            Businesses can use SPI for certain{" "}
            <span
              className="text-blue-600 underline font-semibold cursor-pointer"
              onClick={() => setShowEssentialPurposesModal(true)}
            >
              essential purposes
            </span>{" "}
            without triggering a Right to Limit. But if they use or disclose that SPI for
            profiling—typically related to marketing or advertising activities—consumers can ask the
            business to restrict its use of SPI to essential purposes only.
          </p>
          <p className="mt-2">
            <span className="font-semibold">Example</span>
            <br />
            <p>
              An app collects consumers' GPS location to provide driving directions. It also shares
              the location data with ad networks that use it to serve ads and build profiles on
              consumers. The consumer can request that the app only use their location for the
              purpose of providing directions, not for advertising purposes.
            </p>
          </p>
          <p className="mt-2">
            Does your business use SPI in the systems below for profiling or marketing?
          </p>
        </div>

        {showEssentialPurposesModal && (
          <EssentialPurposesModal closeModal={() => setShowEssentialPurposesModal(false)} />
        )}

        <div className="border border-solid border-gray-200 rounded-lg max-w-[893px] mt-1.5 ml-[40px] max-h-[636px] overflow-y-auto">
          <table>
            <colgroup>
              <col style={{ width: "300px" }} />
              <col style={{ width: "325x" }} />
              <col style={{ width: "208px" }} />
              <col style={{ width: "60px" }} />
            </colgroup>
          </table>
          <thead className="sticky top-0 z-10">
            <tr>
              <th
                scope="col"
                className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tl-lg pl-6 text-left text-xs font-medium text-gray-500 uppercase"
              >
                Vendor
              </th>
              <th
                scope="col"
                className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-left text-xs font-medium text-gray-500 uppercase"
              >
                SPI Used For Profiling or Marketing
              </th>
              <th
                scope="col"
                className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-left text-xs font-medium text-gray-500 uppercase"
              >
                Contact for Requests
              </th>
              <th
                scope="col"
                className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-left text-xs font-medium text-gray-500 uppercase"
              >
                &nbsp;
              </th>
            </tr>
          </thead>
          <tbody>
            {limitVendors.map((dataRecipient) => {
              let limitInstruction = instructions?.find(
                (i) => i.vendorId == dataRecipient.vendorId && i.requestType == "LIMIT",
              );

              if (!limitInstruction) {
                return null;
              }

              return (
                <DataRecipientRowWithSidebar
                  key={dataRecipient.id}
                  dataRecipient={dataRecipient}
                  isNonEssentialSpiPurposeResponseLockedToYes={vendorIdsAutomaticallyTriggerLimitRequest.includes(
                    dataRecipient.vendorId,
                  )}
                  limitInstruction={limitInstruction}
                  updateDataRecipient={async (args: UpdateVendorArgs) => {
                    // Opportunistically update the answersByVendorId map
                    // Note then that if the subsequent API update fails, the UI will be temporarily out of sync
                    setAnswersByVendorId((prev) => {
                      if (!prev) {
                        return null;
                      }
                      const newMap = new Map(prev);
                      newMap.set(
                        dataRecipient.vendorId,
                        args.vendorSettings.spiUsedForNonEssentialPurposes,
                      );
                      return newMap;
                    });
                    await updateDataRecipient(args);
                  }}
                  updateInstruction={updateInstruction}
                  openSidebar={() => setSidebarOpenForVendorId(dataRecipient.vendorId)}
                  isSidebarOpen={sidebarOpenForVendorId === dataRecipient.vendorId}
                  closeSidebar={() => setSidebarOpenForVendorId(null)}
                  apiIntegrations={apiIntegrations}
                />
              );
            })}
          </tbody>
        </div>

        {!effectiveIsLimitRequestsEnabled && (
          <div className="bg-blue-50 flex items-center max-w-[736px] mt-[32px] ml-[40px] p-[16px] rounded-[8px]">
            <div>
              <InformationCircleIcon className="w-[16px] h-[16px] text-blue-400 mr-[14px]" />
            </div>
            <div className="text-blue-700 font-medium text-[14px]">
              Based on your selection above, Request-to-Limit does not apply.
            </div>
          </div>
        )}

        {effectiveIsLimitRequestsEnabled && (
          <>
            <div className="flex items-center gap-3 mt-10">
              <StepNumberCircle text="2" />
              <div className="text-gray-900 text-[18px] font-normal">
                Auto-send request processing emails
              </div>
              <Toggle
                checked={autoSendRequestEmailNotificationsForLimitRequestsValue}
                onChange={async (checked) => {
                  setAutoSendRequestEmailNotificationsForLimitRequestsValue(checked);
                  await Api.organization.updateOrganization(organization.id, {
                    name: organization.name,
                    logoUrl: organization.logoUrl,
                    regulations: [],
                    autoSendRequestEmailNotificationsForLimitRequests: checked,
                  });
                  await refreshOrg();

                  if (checked) {
                    snackbar("Enabled automatically sending limit request email notifications", {
                      variant: "success",
                    });
                  } else {
                    // closeSnackbar functionality not available in new API
                  }
                }}
              />
            </div>
            <div className="text-neutral-900 font-normal text-[14px] max-w-[950px] ml-[40px] mt-[6px]">
              If this is toggled off, you will be prompted to manually click a button to send the
              vendor email and consumer notification email below for each request you process.
            </div>
          </>
        )}
      </div>

      {/* Put the email templates outside of tw-boundary since the template HTML will only render correctly with default styles, and will not render correctly if overriden with tailwind styles */}
      {effectiveIsLimitRequestsEnabled && (
        <div style={{ marginTop: "12px", marginLeft: "40px", marginBottom: "700px" }}>
          <div className="tw-boundary">
            <div className="text-neutral-900 text-[14px]">Vendor Email Notification Template</div>
          </div>

          <div
            style={{
              backgroundColor: "#F9F8FB",
              padding: "32px",
              maxWidth: "736px",
              marginTop: "6px",
            }}
          >
            <p>
              <strong>To:</strong> <span>[recipient email]</span>
            </p>

            <p>
              <strong>Subject: CCPA Request to Limit</strong>
            </p>

            <div dangerouslySetInnerHTML={{ __html: vendorMessageTemplatePreview }}></div>
          </div>
        </div>
      )}

      {showDiagnostics && (
        <LimitInstructionsDiagnostics
          allVendors={allVendors}
          limitRequestInfo={limitRequestInfo}
          dataMap={dataMap}
          showVerboseDiagnostics={showVerboseDiagnostics}
        />
      )}
    </WorkflowSetupPageWrapper>
  );
};

function DataRecipientRowWithSidebar({
  dataRecipient,
  isNonEssentialSpiPurposeResponseLockedToYes,
  limitInstruction,
  updateDataRecipient,
  updateInstruction,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
  apiIntegrations,
}: {
  dataRecipient: OrganizationDataRecipientDto;
  isNonEssentialSpiPurposeResponseLockedToYes: boolean;
  limitInstruction: RequestHandlingInstructionDto;
  updateDataRecipient: (args: UpdateVendorArgs) => Promise<void>;
  updateInstruction: (instruction: RequestHandlingInstructionDto) => void;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  apiIntegrations: PrivacyCenterIntegrationsDto;
}) {
  const [email, setEmail] = useState(dataRecipient.email || dataRecipient.defaultEmail);
  const [spiUsedForNonEssentialPurposesValue, setSpiUsedForNonEssentialPurposesValue] = useState(
    dataRecipient.spiUsedForNonEssentialPurposes,
  );
  const [doNotContactValue, setDoNotContactValue] = useState(limitInstruction.doNotContact);

  const finalSpiUsedForNonEssentialPurposeValue = isNonEssentialSpiPurposeResponseLockedToYes
    ? true
    : spiUsedForNonEssentialPurposesValue;

  const dataRecipientWithLocalChanges = {
    ...dataRecipient,
    spiUsedForNonEssentialPurposesValue: finalSpiUsedForNonEssentialPurposeValue,
  };
  // only set email if it is not empty
  if (email && email.length > 0) {
    dataRecipientWithLocalChanges.email = email;
  }

  const limitInstructionWithLocalChanges: RequestHandlingInstructionDto = {
    ...limitInstruction,
    doNotContact: doNotContactValue,
  };

  const canClickRowToOpenSidebar = finalSpiUsedForNonEssentialPurposeValue === true;

  let vendorApiConnection = (apiIntegrations?.vendors || []).find(
    (apiIntegration) => apiIntegration.vendorId == dataRecipient.vendorId,
  );

  return (
    <>
      <tr
        key={dataRecipient.id}
        className={clsx(
          "border-t border-solid border-gray-200",
          canClickRowToOpenSidebar && "cursor-pointer",
          {
            "bg-neutral-0": isSidebarOpen,
          },
        )}
        onClick={(e) => {
          if (!canClickRowToOpenSidebar) {
            return;
          }

          if (isEventClickTargetOnSelectMenu(e)) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          openSidebar();
        }}
      >
        <td className="px-6 py-3.5 w-[300px] border-b border-solid border-gray-200">
          <div className="flex items-center gap-3">
            <img src={dataRecipient.logoUrl} alt={dataRecipient.name} className="h-6 w-6" />
            <span className="text-[#111827] font-medium text-[14px]">{dataRecipient.name}</span>
            {vendorApiConnection &&
              (vendorApiConnection.connected ? (
                <BoltIcon className="h-4 w-4 text-green-600 bg-[#E2FDF3] p-0.5 rounded-3xl" />
              ) : (
                <BoltSlashIcon className="h-4 w-4 text-neutral-700 bg-[#F5F4F9] p-0.5 rounded-3xl" />
              ))}
          </div>
        </td>
        <td className="text-left min-w-[325px] w-[325px] border-b border-solid border-gray-200">
          <ToolTip
            text={
              isNonEssentialSpiPurposeResponseLockedToYes
                ? `Stores "Inferences based on sensitive information".`
                : null
            }
            width="300px"
            xOffset={125}
            yOffset={75}
          >
            <SelectMenuDropdown<"YES" | "NO">
              items={[
                {
                  label: "Yes",
                  value: "YES",
                  isSelected: finalSpiUsedForNonEssentialPurposeValue === true,
                },
                {
                  label: "No",
                  value: "NO",
                  isSelected: finalSpiUsedForNonEssentialPurposeValue === false,
                },
              ]}
              noSelectedErrorMessage="Select answer"
              isLocked={isNonEssentialSpiPurposeResponseLockedToYes}
              onSelect={async (newSelectedValue: "YES" | "NO") => {
                setSpiUsedForNonEssentialPurposesValue(newSelectedValue === "YES");
                await updateDataRecipient({
                  vendorId: dataRecipient.vendorId,
                  vendorSettings: {
                    ...dataRecipient,
                    spiUsedForNonEssentialPurposes: newSelectedValue === "YES",
                    classificationId: dataRecipient.classification.id,
                  },
                });
              }}
              dropdownWidthPx={232}
              rightPositionPx={-106}
            />
          </ToolTip>
        </td>
        <td className="text-center w-[208px] max-w-[208px] border-b border-solid border-gray-200 break-all">
          {finalSpiUsedForNonEssentialPurposeValue === true ? (
            contactForRequestsColumnValue(
              dataRecipientWithLocalChanges,
              limitInstructionWithLocalChanges,
            )
          ) : (
            <hr className="w-[51px] text-neutral-700 mx-auto" />
          )}
        </td>
        <td className="w-[60px] max-w-[60px] border-b border-solid border-gray-200">
          {canClickRowToOpenSidebar && (
            <ArrowRightCircleIcon
              className="w-[20px] h-[20px] text-primary-600 cursor-pointer"
              onClick={openSidebar}
            />
          )}
        </td>
      </tr>

      <Slideout
        open={isSidebarOpen}
        setOpen={(newIsOpen: boolean) => {
          if (newIsOpen) {
            openSidebar();
          } else {
            closeSidebar();
          }
        }}
        slideFrom="right"
      >
        <>
          <div className="bg-neutral-0 p-6">
            <div className="flex justify-between items-center">
              <h3 className="text-gray-900 text-[18px] font-bold">
                Request to Limit Vendor Settings
              </h3>
              <div className="cursor-pointer" onClick={closeSidebar}>
                <XMarkIcon className="w-6 h-6 text-[#9CA3AF]" />
              </div>
            </div>

            <div className="mt-1">
              <div className="flex items-center">
                <img
                  src={dataRecipientWithLocalChanges.logoUrl}
                  alt={dataRecipientWithLocalChanges.name}
                  className="h-5 w-5"
                />
                <span className="text-gray-900 font-medium text-[18px] ml-[12px] mr-[12px]">
                  {dataRecipientWithLocalChanges.name}
                </span>
              </div>
              {dataRecipientWithLocalChanges.url?.length > 0 && (
                <div className="mt-1 text-[14px] font-normal text-gray-500">
                  {dataRecipientWithLocalChanges.url}
                </div>
              )}
            </div>
          </div>
          <div>
            <Form
              onSubmit={() => {}}
              render={() => {
                return (
                  <>
                    <div className="p-6">
                      <div>
                        <label className="block text-[14px] font-medium text-gray-700">
                          Contact Email for Requests
                        </label>
                        <TextInput
                          field="vendor-limit-contact-email"
                          onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                            let newEmail = e.target.value;
                            setEmail(newEmail);
                            let updatedDataRecipient = {
                              ...dataRecipientWithLocalChanges,
                              email: newEmail,
                            };
                            await updateDataRecipient({
                              vendorId: dataRecipientWithLocalChanges.vendorId,
                              vendorSettings: {
                                ...updatedDataRecipient,
                                classificationId: dataRecipientWithLocalChanges.classification.id,
                              },
                            });
                          }}
                          defaultValue={email}
                          maxLength={90}
                          disabled={doNotContactValue}
                        />
                      </div>
                      <div className="mt-6">
                        <SimpleCheckbox
                          name="doNotContact"
                          label={`Do Not Contact ${dataRecipient.name} for Limit Requests`}
                          initialChecked={limitInstruction?.doNotContact}
                          onChange={async () => {
                            let newDoNotContact = !doNotContactValue;

                            setDoNotContactValue(newDoNotContact);

                            let updatedInstruction = {
                              ...limitInstructionWithLocalChanges,
                              doNotContact: newDoNotContact,
                            };

                            await updateInstruction(updatedInstruction);
                          }}
                        />
                      </div>
                    </div>
                  </>
                );
              }}
            />
          </div>
        </>
      </Slideout>
    </>
  );
}

const contactForRequestsColumnValue = (
  vendor: OrganizationDataRecipientDto,
  limitInstruction: RequestHandlingInstructionDto,
): React.ReactNode => {
  let resultLabel = "",
    textColor = "",
    bgColor = "";
  let email = vendor.email || vendor.defaultEmail;

  if (limitInstruction?.doNotContact) {
    resultLabel = "Do Not Contact";
    textColor = "text-gray-800";
    bgColor = "bg-neutral-200";
  } else if (email) {
    resultLabel = email;
    textColor = "text-[#1E40AF]";
    bgColor = "bg-[#DBEAFE]";
  } else {
    resultLabel = "Email Address Needed";
    textColor = "text-[#92400E]";
    bgColor = "bg-[#FEF3C7]";
  }

  return (
    <div
      className={`inline-block px-2.5 py-0.5 max-w-[188px] truncate font-medium text-sm rounded-full ${textColor} ${bgColor}`}
    >
      {resultLabel}
    </div>
  );
};

function ListOfSensitivePicsModal({
  closeModal,
  dataMap,
}: {
  closeModal: () => void;
  dataMap: DataMapDto;
}) {
  // Only show PICs that are sensitive in California
  const californiaSensitivePics = getSensitivePICsWithFilters(dataMap, {
    mustBeSensitiveInCA: true,
    includeInferencesBasedOnSensitiveInformationPIC: true,
  })
    .map((cat) => cat.name)
    .sort();

  return (
    <Modal
      title="List of Sensitive Personal Information Categories"
      alignText="left"
      firstButton="OK"
      widthButton="full"
      onFirstButtonClick={closeModal}
      showOverlay
      closeButton
      onClose={closeModal}
    >
      <div className="pt-4">
        <ul className="list-disc list-inside">
          {californiaSensitivePics.map((pic, i) => (
            <li key={i}>{pic}</li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

function EssentialPurposesModal({ closeModal }: { closeModal: () => void }) {
  return (
    <Modal
      alignText="left"
      firstButton="OK"
      widthButton="full"
      onFirstButtonClick={closeModal}
      showOverlay
      closeButton
      onClose={closeModal}
    >
      <div className="pt-4">
        Here are the essential purposes for which businesses can use SPI without triggering a Right
        to Limit:
        <ol className="list-decimal list-outside mt-1.5 pl-4">
          <li>
            To provide goods or services, as reasonably expected by the consumer in that context.
          </li>
          <li>To prevent, detect, and investigate data security incidents.</li>
          <li>To resist malicious, deceptive, fraudulent, or illegal actions.</li>
          <li>To ensure people's physical safety.</li>
          <li>
            For short-term, transient use, include non-personalized advertising shown within the
            business's own website (if not shared with any third party and not used to build a
            profile).
          </li>
          <li>
            To perform services on behalf the business, such processing payments, storing data, and
            providing customer service.
          </li>
          <li>
            To verify or maintain the quality or safety of the business's products or services, and
            to improve or enhance the product or service.
          </li>
          <li>
            Collecting or processing SPI where the purpose is not to infer characteristics about the
            consumer.
          </li>
        </ol>
      </div>
    </Modal>
  );
}

function LimitInstructionsDiagnostics({
  allVendors,
  dataMap,
  limitRequestInfo,
  showVerboseDiagnostics,
}: {
  allVendors: OrganizationDataRecipientDto[];
  dataMap: DataMapDto;
  limitRequestInfo: VendorsAndDisclosedPICsForLimitRequests;
  showVerboseDiagnostics: boolean;
}) {
  const [vendorIdsProcessingSensitivePICs, processedSensitivePICIds] =
    getVendorsAndPICsForLimitRequests(dataMap);

  return (
    <div className="tw-boundary">
      <div className="max-w-[80ch] mt-12">
        <div className="text-neutral-800 font-black text-[18px] mt-12">
          Diagnostics for Limit Requests
        </div>
        <div className="space-y-3">
          <div>
            <div>Parsing Data Map</div>
            <div className="pl-3">
              Vendors processing sensitive PICs:{" "}
              {allVendors
                .filter((v) => vendorIdsProcessingSensitivePICs.includes(v.vendorId))
                .map((v) => v.name)
                .join(", ")}
              <br />
              Processed sensitive PICs:{" "}
              {Object.values(dataMap?.categories || {})
                .filter((cat) => processedSensitivePICIds.includes(cat.id))
                .map((cat) => cat.name)
                .join(", ")}
            </div>
            {showVerboseDiagnostics && (
              <div>
                <div>All Disclosures</div>
                <div className="pl-3">
                  {dataMap.disclosure.map((disclosure) => {
                    let collectionGroup = dataMap.collectionGroups[disclosure.groupId];
                    let pic = dataMap.categories[disclosure.picId];
                    let vendor = allVendors.find((v) => v.vendorId === disclosure.vendorId);
                    if (!collectionGroup || !pic) return null;
                    return (
                      <div key={`${disclosure.vendorId}-${disclosure.groupId}-${disclosure.picId}`}>
                        {vendor?.name || "UNKNOWN VENDOR"} is being disclosed {pic.name} from{" "}
                        {collectionGroup.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div>
            Using Legacy Limit Vendors Behavior:{" "}
            {limitRequestInfo.shouldUseLegacyLimitVendors ? "Yes" : "No"}
            {limitRequestInfo.shouldUseLegacyLimitVendors && (
              <div className="pl-3">
                <p>
                  Explanation: the legacy way of determining limit vendors is used if the org has
                  some vendors that are being disclosed a sensitive PIC, but it is not the
                  "Inferences based on sensitive information" PIC, and there are some missing
                  non-essential purpose responses. (Note that this only refers to how limit vendors
                  are determined for processing limit requests, see "Is Limit Requests Enabled" to
                  see if limit requests are enabled.)
                </p>
                <p>
                  To remove the org from this legacy behavior, they need to fill out the
                  non-essential SPI purposes responses for the following vendors:{" "}
                  {allVendors
                    .filter(
                      (v) =>
                        limitRequestInfo.vendorIdsRequiringResponseForNonEssentialSpiQuestion.includes(
                          v.vendorId,
                        ) && v.spiUsedForNonEssentialPurposes == null,
                    )
                    .map((v) => v.name)
                    .join(", ")}
                </p>
              </div>
            )}
          </div>
          <div>
            Is Limit Requests Enabled: {limitRequestInfo.isLimitRequestsEnabled ? "Yes" : "No"}
          </div>
        </div>
      </div>
    </div>
  );
}
